import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  IExperienceDenormalizedV2,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import { Typography } from '@material-ui/core';
import { partition } from 'lodash';
import { useMemo } from 'react';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { FlexContainerVertical } from '../../../../../../layout/Flex';
import { CompletedSection } from '../CompletedSection';
import { DoneCelebration } from '../DoneCelebration';
import { IncompleteSection } from '../IncompleteSection';
import { InstanceWeek } from '../Nav/WeekNav';

export const AllDone = () => {
  const { t } = useUnsafeTranslation();
  return (
    <DoneCelebration title={t(`All done!`)}>
      <Typography>
        {t(`You've completed all your tasks in this experience.`)}
      </Typography>
      <Typography>
        {t(`We'll let you know if any new tasks need your attention.`)}
      </Typography>
    </DoneCelebration>
  );
};

const WeekDone = ({ hideSubtext }: { hideSubtext?: boolean }) => {
  const { t } = useUnsafeTranslation();
  return (
    <DoneCelebration title={t(`You're done for now!`)}>
      {!hideSubtext && (
        <Typography>
          {t(`All your tasks for this week are completed`)}
        </Typography>
      )}
    </DoneCelebration>
  );
};

export const InstanceTimelineGroup = ({
  instance,
  weekGroup,
  actorAccessToken,
  instanceAccessToken,
  isFuture,
  returnTo
}: {
  instance: Doc<IExperienceDenormalizedV2>;
  weekGroup: InstanceWeek;
  actorAccessToken: string;
  instanceAccessToken: string;
  isFuture: boolean;
  returnTo?: string;
}) => {
  const { t } = useUnsafeTranslation();
  const [completedTasks, incompleteTasks] = useMemo(() => {
    return partition(
      weekGroup.tasks,
      (tsk) => tsk.status === TaskStatus.COMPLETE
    );
  }, [weekGroup]);
  const allComplete = useMemo(
    () =>
      instance.data.tasks.every((tsk) => tsk.status === TaskStatus.COMPLETE),
    [instance.data.tasks]
  );
  const anyOverdue = useMemo(
    () => instance.data.tasks.some((tsk) => tsk.status === TaskStatus.LATE),
    [instance.data.tasks]
  );
  return (
    <FlexContainerVertical fullWidth spacing={3}>
      <Typography variant="h3">
        {t('Week {{ week }}', { week: weekGroup.count })}
      </Typography>
      <FlexContainerVertical fullWidth spacing={4}>
        {allComplete ? (
          <AllDone />
        ) : !incompleteTasks.length ? (
          (isFuture || anyOverdue) && !weekGroup.tasks.length ? null : (
            <WeekDone hideSubtext={!weekGroup.tasks.length} />
          )
        ) : null}
        <IncompleteSection
          tasks={incompleteTasks}
          messages={weekGroup.messages}
          actorAccessToken={actorAccessToken}
          instanceAccessToken={instanceAccessToken}
          returnTo={returnTo}
        />
        {!!completedTasks.length && (
          <FlexContainerVertical fullWidth spacing={2}>
            <CompletedSection
              key={weekGroup.count}
              completedTasks={completedTasks}
              actorAccessToken={actorAccessToken}
              instanceAccessToken={instanceAccessToken}
              areThereAnyIncompleteTasks={!!incompleteTasks.length}
              returnTo={returnTo}
            />
          </FlexContainerVertical>
        )}
      </FlexContainerVertical>
    </FlexContainerVertical>
  );
};
